'use client'

import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { GradientButton } from '@/components/ui/gradient-button'
import { Plus, Trash2, Upload, Download, Send, Calculator, ShoppingCart, X, Package, Rocket } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose, DialogPortal } from '@/components/ui/dialog'
import { toast } from 'sonner'
import { Quote, QuoteItem, CustomerInfo } from '@/lib/types'
import { Breadcrumb } from '@/components/breadcrumb'
import { useQuoteCart } from '@/lib/quote-cart-context'
import { parseBOMCSV, REQUIRED_HEADERS, parseBOMFile } from '@/lib/csv-utils'
import { useRecaptcha } from '@/hooks/use-recaptcha'
import { generateQuotePDF } from '@/lib/pdf-utils'

export default function QuotePage() {
  const { quoteItems: cartItems, clearQuoteCart, removeFromQuoteCart, updateQuantity, updateNotes } = useQuoteCart()
  const [quoteItems, setQuoteItems] = useState<QuoteItem[]>([])
  
  const router = useRouter()
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    industry: '',
    country: ''
  })

  const [additionalInfo, setAdditionalInfo] = useState({ address: '', notes: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { getToken } = useRecaptcha()
  const [showClearDialog, setShowClearDialog] = useState(false)
  const [bomPreviewRows, setBOMPreviewRows] = useState<any[]>([])
  const [bomRowErrors, setBOMRowErrors] = useState<Record<number, string>>({})

  const isCustomerInfoValid = () => {
    return customerInfo.company && customerInfo.firstName && customerInfo.email
  }

  useEffect(() => {
    const itemsFromCart = cartItems.map(item => ({
      id: item.product.id,
      partNumber: item.product.partNumber,
      description: item.product.description,
      manufacturer: item.product.manufacturer,
      quantity: item.quantity,
      unitPrice: 0,
      leadTime: '',
      notes: item.notes || ''
    }))
    setQuoteItems(itemsFromCart)
  }, [cartItems])

  const addQuoteItem = () => {
    const newItem: QuoteItem = {
      id: `new-${Date.now()}`,
      partNumber: '',
      description: '',
      manufacturer: '',
      quantity: 1,
      unitPrice: 0,
      leadTime: '',
      notes: ''
    }
    setQuoteItems([...quoteItems, newItem])
  }

  const removeQuoteItem = (id: string) => {
    setQuoteItems(quoteItems.filter(item => item.id !== id))
    removeFromQuoteCart(id)
  }

  const updateQuoteItem = (id: string, field: keyof QuoteItem, value: any) => {
    setQuoteItems(quoteItems.map(item => item.id === id ? { ...item, [field]: value } : item))
    if (field === 'quantity') {
      updateQuantity(id, value)
    } else if (field === 'notes') {
      updateNotes(id, value)
    }
  }

  const confirmClearCart = () => {
    clearQuoteCart()
    setQuoteItems([])
    setShowClearDialog(false)
  }

  const updateBOMPreviewRow = (index: number, field: string, value: any) => {
    const newRows = [...bomPreviewRows]
    newRows[index][field] = value
    setBOMPreviewRows(newRows)
  }

  const removeBOMPreviewRow = (index: number) => {
    const newRows = [...bomPreviewRows]
    newRows.splice(index, 1)
    setBOMPreviewRows(newRows)
  }

  const handleSubmitQuote = async () => {
    setIsSubmitting(true)

    try {
      const recaptchaToken = await getToken('quote_submit')
      if (!recaptchaToken && process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) {
        toast.error('reCAPTCHA challenge failed. Please try again.')
        setIsSubmitting(false)
        return
      }

      const quote = {
        customerInfo,
        items: quoteItems.map(({ id, ...item }) => item), // Remove client-side id
        additionalInfo,
        recaptchaToken,
      }

      const response = await fetch('/api/quotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quote)
      })

      if (!response.ok) {
        throw new Error('Failed to submit quote')
      }

      const data = await response.json()
      
      // Clear the cart after successful submission
      clearQuoteCart()
      
      toast.success('Quote request submitted successfully!')
      
      // Navigate to confirmation page
      router.push(`/quote/confirmation/${data.quoteId}`)
    } catch (error) {
      console.error('Quote submission error:', error)
      toast.error('Failed to submit quote. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // BOM Upload Dialog State
  const [showBOMDialog, setShowBOMDialog] = useState(false);
  const [bomProcessing, setBOMProcessing] = useState(false);
  const [bomError, setBOMError] = useState<string | null>(null);
  const [bomFileName, setBOMFileName] = useState<string | null>(null);

  const handleBOMUpload = () => {
    setShowBOMDialog(true);
  }

  const handleExportQuote = () => {
    if (quoteItems.length === 0) {
      toast.error("Please add items to the quote before exporting.")
      return
    }
    generateQuotePDF(customerInfo, quoteItems)
  }

  // Example CSV for instructions
  const exampleCSV = `partNumber,description,manufacturer,quantity\nSTM32F103C8T6,32-bit ARM Cortex-M3 MCU,STMicroelectronics,10\nSN74HC595,8-bit shift register,Texas Instruments,25`;

  // BOM Dialog Component
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [bomProgress, setBOMProgress] = useState<number>(0);

  const BOMUploadDialog = () => (
    <Dialog open={showBOMDialog} onOpenChange={setShowBOMDialog}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload BOM (CSV)</DialogTitle>
          <DialogDescription>Upload a CSV file with the following required headers:</DialogDescription>
        </DialogHeader>

        <div className="mb-2 text-xs text-gray-600">
          <ul className="mb-2 text-sm list-disc list-inside text-gray-700">
            <li><span className="font-semibold">partNumber</span> <span className="text-gray-500">(required)</span></li>
            <li><span className="font-semibold">description</span> <span className="text-gray-500">(required)</span></li>
            <li><span className="font-semibold">manufacturer</span> <span className="text-gray-500">(required)</span></li>
            <li><span className="font-semibold">quantity</span> <span className="text-gray-500">(required, integer {'>'} 0)</span></li>
          </ul>
          <div>
            <span className="font-semibold">Example CSV:</span>
            <pre className="bg-slate-50 border rounded p-2 mt-1 whitespace-pre-wrap">{exampleCSV}</pre>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="inline-flex items-center px-3 py-2 rounded-md border bg-white text-sm shadow-sm hover:bg-slate-50"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Choose BOM file"
            >
              Choose file
            </button>
            <div className="text-sm text-gray-700">{bomFileName ? <span className="text-blue-700">{bomFileName}</span> : <span className="text-gray-500">No file selected</span>}</div>
          </div>
          <input
            ref={fileInputRef}
            id="bom-file-input"
            type="file"
            accept=".csv,.xlsx,.xls"
            className="sr-only"
            onChange={async e => {
              setBOMError(null);
              setBOMFileName(null);
              setBOMPreviewRows([]);
              setBOMRowErrors({});
              const file = e.target.files?.[0];
              if (!file) return;
              setBOMFileName(file.name);
              setBOMProcessing(true);
              setBOMProgress(0);
              try {
                  const { rows, errors, duplicates } = await parseBOMFile(file, p => {
                    setBOMProgress(Math.round(p * 100));
                  });

                // Prepare row-level errors
                const rowErrors: { [idx: number]: string } = {};

                // Mark duplicates
                if (duplicates && duplicates.length > 0) {
                  duplicates.forEach((dup: string) => {
                    rows.forEach((r: any, idx: number) => {
                      if (r.partNumber === dup) rowErrors[idx] = 'Duplicate part number';
                    });
                  });
                }

                // Validate each row
                rows.forEach((row: any, idx: number) => {
                  if (!row.partNumber || !row.description || !row.manufacturer || row.quantity === undefined || row.quantity === null) {
                    rowErrors[idx] = rowErrors[idx] ? rowErrors[idx] + '; Missing fields' : 'Missing required fields';
                  } else if (isNaN(Number(row.quantity)) || Number(row.quantity) <= 0) {
                    rowErrors[idx] = rowErrors[idx] ? rowErrors[idx] + '; Invalid quantity' : 'Invalid quantity';
                  }
                });

                // Set preview and errors
                setBOMPreviewRows(rows);
                setBOMRowErrors(rowErrors);
                setBOMProcessing(false);
                setBOMProgress(100);

                if (errors.length > 0) {
                  setBOMError(errors.join('\n'));
                  toast.error('BOM file invalid. Please check errors and see example CSV structure.');
                  return;
                }

                if (Object.keys(rowErrors).length > 0) {
                  setBOMError('Some rows have errors. Please review and correct them or download the corrected CSV.');
                  toast.error('BOM file has row errors. See preview for details.');
                } else {
                  setBOMError(null);
                  toast.success('BOM parsed successfully. Review and confirm import.');
                }
              } catch (err) {
                console.error('parse error', err);
                setBOMProcessing(false);
                setBOMError('Failed to read or parse file.');
                toast.error('Failed to process BOM file.');
              }
            }}
          />
          {/* Progress bar */}
          {bomProcessing && (
            <div className="mt-3">
              <div className="w-full bg-slate-100 rounded h-2 overflow-hidden">
                <div className="bg-blue-500 h-2" style={{ width: `${bomProgress}%` }} />
              </div>
              <div className="text-xs text-gray-600 mt-1">Parsing file... {bomProgress}%</div>
            </div>
          )}

          {/* Preview Table and buttons (kept same) */}
          {bomPreviewRows.length > 0 && (
            <div className="mt-4">
              <div className="font-semibold mb-2">Preview & Edit BOM Rows:</div>
              <div className="overflow-x-auto">
                <div className="max-h-[50vh] overflow-auto border rounded">
                  <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border px-2 py-1">#</th>
                    <th className="border px-2 py-1">Part Number</th>
                    <th className="border px-2 py-1">Description</th>
                    <th className="border px-2 py-1">Manufacturer</th>
                    <th className="border px-2 py-1">Quantity</th>
                    <th className="border px-2 py-1">Error</th>
                    <th className="border px-2 py-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bomPreviewRows.map((row, idx) => (
                    <tr key={idx} className={bomRowErrors[idx] ? 'bg-red-50' : ''}>
                      <td className="border px-2 py-1">{idx + 1}</td>
                      <td className="border px-2 py-1">
                        <input type="text" value={row.partNumber} onChange={e => updateBOMPreviewRow(idx, 'partNumber', e.target.value)} className="w-24 border rounded px-1" />
                      </td>
                      <td className="border px-2 py-1">
                        <input type="text" value={row.description} onChange={e => updateBOMPreviewRow(idx, 'description', e.target.value)} className="w-32 border rounded px-1" />
                      </td>
                      <td className="border px-2 py-1">
                        <input type="text" value={row.manufacturer} onChange={e => updateBOMPreviewRow(idx, 'manufacturer', e.target.value)} className="w-24 border rounded px-1" />
                      </td>
                      <td className="border px-2 py-1">
                        <input type="number" min={1} value={row.quantity} onChange={e => updateBOMPreviewRow(idx, 'quantity', parseInt(e.target.value) || 1)} className="w-16 border rounded px-1" />
                      </td>
                      <td className="border px-2 py-1 text-red-600">{bomRowErrors[idx] || ''}</td>
                      <td className="border px-2 py-1">
                        <Button variant="ghost" size="icon-sm" onClick={() => removeBOMPreviewRow(idx)} aria-label="Remove row">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                  </table>
                </div>
              </div>
              {/* Download corrected CSV button if errors exist */}
              {Object.keys(bomRowErrors).length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    // Download only valid rows as CSV
                    const validRows = bomPreviewRows.filter((_, idx) => !bomRowErrors[idx]);
                    const csv = [REQUIRED_HEADERS.join(',')].concat(validRows.map(r => `${r.partNumber},${r.description},${r.manufacturer},${r.quantity}`)).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'corrected_bom.csv';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }}
                >
                  Download Corrected CSV
                </Button>
              )}
              {/* Confirm Import Button */}
              {Object.keys(bomRowErrors).length === 0 && bomPreviewRows.length > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  className="mt-4"
                  onClick={() => {
                    setQuoteItems(bomPreviewRows.map((row, idx) => ({
                      id: `${row.partNumber}-${idx}-${Date.now()}`,
                      partNumber: row.partNumber,
                      description: row.description,
                      manufacturer: row.manufacturer,
                      quantity: row.quantity,
                      unitPrice: 0,
                      leadTime: '',
                      notes: ''
                    })));
                    setShowBOMDialog(false);
                    toast.success('BOM imported and quote items populated!');
                  }}
                >
                  Confirm Import
                </Button>
              )}
            </div>
          )}
          {bomFileName && <div className="mt-2 text-xs text-blue-700">Selected file: {bomFileName}</div>}
          {bomError && <div className="mt-2 text-xs text-red-600">{bomError}</div>}
        </div>
        {bomProcessing && (
          <div className="mt-4 flex items-center gap-2 text-blue-600">
            <span className="animate-spin"><Rocket className="w-4 h-4" /></span>
            Processing BOM file...
          </div>
        )}

        <DialogFooter className="mt-4">
          <DialogClose asChild>
            <Button variant="outline" size="sm">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Breadcrumb items={[
        { href: "/", label: "Home" },
        { label: "Request Quote", current: true }
      ]} />

      <div className="container mx-auto px-4 py-8">
        {showBOMDialog && <BOMUploadDialog />}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">Request Quote</h1>
          <p className="text-gray-600">
            Get personalized pricing for your electronic component requirements
          </p>
        </div>

        {/* Cart Preview Banner */}
        {cartItems.length > 0 && (
          <Card className="mb-8 bg-blue-50 border-blue-200 animate-in fade-in">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-blue-500 p-2 rounded-lg">
                  <ShoppingCart className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-blue-900 mb-1">
                    {cartItems.length} item{cartItems.length !== 1 ? 's' : ''} loaded from your cart
                  </p>
                  <p className="text-sm text-blue-800">
                    You can adjust quantities below.
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowClearDialog(true)}>
                <X className="w-4 h-4 mr-2" />
                Clear Cart
              </Button>
            </CardContent>
          </Card>
        )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Quote Form */}
        <div className="lg:col-span-2 space-y-8">
          {/* Customer Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Customer Information
                <Badge variant="outline">Required</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="companyName">Company Name *</Label>
                  <Input
                    id="companyName"
                    value={customerInfo.company}
                    onChange={(e) => setCustomerInfo({...customerInfo, company: e.target.value})}
                    placeholder="Your Company Inc."
                  />
                </div>
                <div>
                  <Label htmlFor="contactName">Contact Name *</Label>
                  <Input
                    id="contactName"
                    value={customerInfo.firstName}
                    onChange={(e) => setCustomerInfo({...customerInfo, firstName: e.target.value})}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                    placeholder="john@company.com"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  value={additionalInfo.address}
                  onChange={(e) => setAdditionalInfo({...additionalInfo, address: e.target.value})}
                  placeholder="123 Main St, City, State, ZIP"
                />
              </div>
              <div>
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  value={additionalInfo.notes}
                  onChange={(e) => setAdditionalInfo({...additionalInfo, notes: e.target.value})}
                  placeholder="Any special requirements or notes..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Quote Items */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <CardTitle>Quote Items</CardTitle>
                <Button variant="outline" size="sm" onClick={handleBOMUpload}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload BOM
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {quoteItems.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <div className="max-w-sm mx-auto">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                      <Package className="w-10 h-10 text-blue-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      No Items Added Yet
                    </h3>
                    <p className="text-slate-600 mb-6">
                      Start building your quote by adding items. You can also upload a BOM file to add multiple items at once.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <GradientButton 
                        size="lg" 
                        onClick={addQuoteItem}
                        variant="blue-red"
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Add Your First Item
                      </GradientButton>
                      <Button 
                        variant="outline" 
                        size="lg" 
                        onClick={handleBOMUpload}
                        className="hover:bg-slate-100 transition-all duration-300"
                      >
                        <Upload className="w-5 h-5 mr-2" />
                        Upload BOM
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {quoteItems.map((item, index) => {
                    // Check if this item came from cart
                    const isFromCart = cartItems.some(cartItem => cartItem.product.id === item.id)
                    
                    return (
                      <div key={item.id} className="border rounded-lg p-4 space-y-4 hover:border-blue-300 transition-colors bg-white">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">Item {index + 1}</h4>
                            {isFromCart && (
                              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                                From Cart
                              </Badge>
                            )}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeQuoteItem(item.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor={`part-${item.id}`}>Part Number *</Label>
                            <Input
                              id={`part-${item.id}`}
                              value={item.partNumber}
                              onChange={(e) => updateQuoteItem(item.id, 'partNumber', e.target.value)}
                              placeholder="STM32F103C8T6"
                              className={!item.partNumber ? 'border-orange-300' : ''}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor={`mfr-${item.id}`}>Manufacturer</Label>
                            <Input
                              id={`mfr-${item.id}`}
                              value={item.manufacturer}
                              onChange={(e) => updateQuoteItem(item.id, 'manufacturer', e.target.value)}
                              placeholder="e.g., STMicroelectronics"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor={`qty-${item.id}`}>Quantity *</Label>
                            <Input
                              id={`qty-${item.id}`}
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateQuoteItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                              className={item.quantity <= 0 ? 'border-orange-300' : ''}
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor={`desc-${item.id}`}>Description</Label>
                            <Input
                              id={`desc-${item.id}`}
                              value={item.description}
                              onChange={(e) => updateQuoteItem(item.id, 'description', e.target.value)}
                              placeholder="32-bit ARM Cortex-M3 MCU, 64KB Flash, 20KB RAM"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor={`lead-${item.id}`}>Required Lead Time</Label>
                            <Select 
                              value={item.leadTime} 
                              onValueChange={(value) => updateQuoteItem(item.id, 'leadTime', value)}
                            >
                              <SelectTrigger id={`lead-${item.id}`}>
                                <SelectValue placeholder="Select lead time" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="urgent">Urgent (1-2 days)</SelectItem>
                                <SelectItem value="1-week">1 Week</SelectItem>
                                <SelectItem value="2-weeks">2 Weeks</SelectItem>
                                <SelectItem value="1-month">1 Month</SelectItem>
                                <SelectItem value="flexible">Flexible</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor={`notes-${item.id}`}>Notes / Special Requirements</Label>
                            <Textarea
                              id={`notes-${item.id}`}
                              value={item.notes}
                              onChange={(e) => updateQuoteItem(item.id, 'notes', e.target.value)}
                              placeholder="RoHS compliant, tape & reel packaging..."
                              className="min-h-[80px] resize-y"
                              rows={2}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  
                  {/* Add More Items Button */}
                  <div className="pt-2">
                    <Button 
                      variant="outline" 
                      onClick={addQuoteItem} 
                      className="w-full border-dashed border-2 hover:border-blue-400 hover:bg-blue-50 transition-all"
                      size="lg"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Add Another Item
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quote Summary Sidebar */}
        <div className="lg:col-span-1">
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Quote Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium">Total Items:</span>
                  <span className="text-lg font-bold text-blue-600">{quoteItems.length}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium">Total Units:</span>
                  <span className="text-lg font-bold text-blue-600">
                    {quoteItems.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium">From Cart:</span>
                  <span className="text-sm font-semibold text-green-600">
                    {cartItems.length} item{cartItems.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <div className="text-xs text-gray-600 space-y-1">
                  <div className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-gray-400 mt-1.5"></div>
                    <span>Complete all required fields (*)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-gray-400 mt-1.5"></div>
                    <span>Include accurate part numbers</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-gray-400 mt-1.5"></div>
                    <span>Specify lead time requirements</span>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2 pt-2">
                <GradientButton 
                  onClick={handleSubmitQuote}
                  disabled={isSubmitting || quoteItems.length === 0 || !isCustomerInfoValid()}
                  size="lg"
                  variant="blue-red"
                  className="w-full group"
                >
                  <Rocket className="w-4 h-4 mr-2 group-hover:animate-bounce transition-transform" />
                  <span className="group-hover:tracking-wide transition-all duration-200">
                    {isSubmitting ? 'Submitting...' : 'Send Quote Request'}
                  </span>
                </GradientButton>
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={handleExportQuote}
                  disabled={quoteItems.length === 0 || !isCustomerInfoValid()}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export as PDF
                </Button>
              </div>
              
              <div className="text-xs text-center text-gray-500 pt-2 border-t">
                <p className="font-medium text-gray-700 mb-1">Quick Response</p>
                <p>You'll receive a detailed quote within 24 hours during business days</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>

      {/* Clear Cart Confirmation Dialog */}
      <ConfirmDialog
        open={showClearDialog}
        onOpenChange={setShowClearDialog}
        onConfirm={confirmClearCart}
        title="Clear Quote Cart?"
        description="This will remove all items from your quote cart and reset the form. Any unsaved changes will be lost."
        confirmText="Clear Cart"
        cancelText="Keep Items"
        variant="danger"
        icon="trash"
      />
    </div>
  )
}